import {
  Dispatch,
  lazy,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

import { SearchResult } from '@lessonPlanner/commons';
import {
  DLLFormat,
  FeatureType,
  TOPIC_EXPLORER_PROMPT_NAME_PREMIUM,
} from '@lessonPlanner/constants';
import { Language, OrganizationSource } from '@lessonPlanner/enums';
import {
  ANCHOR_KEYWORD,
  LessonFormat,
  PROMPT_ACTION,
  PROMPT_TO_BE_USED,
  TARGET_SECTION,
} from '@lessonPlanner/prompts';

import { streamChatCompletion } from '../api/external/GPT';
import { ACTIVITY_SECTION_PER_FORMAT } from '../constants/activities';
import { routeToFeatureType } from '../constants/featureMap';
import { pageContentLocalStorageProperties } from '../constants/localStorage';
import { PAGE_KEY_TITLES } from '../constants/pagekeyTitles';
import { PAGE_KEY_TO_ROUTE_MAP } from '../constants/recentGenerations';
import { routes } from '../constants/routes';
import CreditContext from '../context/credits/context';
import { useGeneratePause } from '../context/generate/provider';
import PortalContext from '../context/portal/context';
import {
  AnalogyContextualizerProperties,
  CatchUpFridaysProperties,
  CerebroLessonPlanProperties,
  DllProperties,
  LessonPlanProperties,
  ObjectiveProperties,
  PageKey,
  QuizMakerSingleProperties,
  SummativeTestProperties,
} from '../enums/pageKey';
import { TOP_UP_TRACKING } from '../enums/topUpTracking';
import { useCerebroRedirect } from '../hooks/main-content/useCerebroRedirect';
import { useGenerationLoader } from '../hooks/main-content/useGenerationLoader';
import { useGenerationSave } from '../hooks/main-content/useGenerationSave';
import { useLocalStorageInitialization } from '../hooks/main-content/useLocalStorageInitialization';
import { usePerplexityStreamHandler } from '../hooks/main-content/usePerplexityStreamHandler';
import { useResponseHandling } from '../hooks/main-content/useResponseHandling';
import { useStreamHandler } from '../hooks/main-content/useStreamHandler';
import { useUpdateLoadingState } from '../hooks/main-content/useUpdateLoadingState';
import {
  checkInAppBrowser,
  useInAppBrowser,
} from '../hooks/useCheckInAppBrowser';
import { useGenerationTracking } from '../hooks/useGenerationTracking';
import { useGetPremiumRoutes } from '../hooks/useGetPremiumRoutes';
import useLocalStorage from '../hooks/useLocalStorage';
import useLocalStorageItem from '../hooks/useLocalStorageItem';
import { useShouldUseCache } from '../hooks/useShouldUseCache';
import {
  ChatCompletionResponseMessageRoleEnum,
  ChatConversation,
  ChatMessage,
} from '../types/chat-completion.type';
import {
  DLLObjective,
  dllProperties,
  FormContents,
  lessonPlanProperties,
  topicExplorerProperties,
  unitPlanProperties,
} from '../types/page';
import {
  elaborateActivitySection4A,
  elaborateExploreSection5OR7E,
  extractActivityNumber,
  modifyActivities,
} from '../utils/activities';
import { getAiMessage } from '../utils/ai';
import { signInUser } from '../utils/auth.utils';
import { handleResponseError as handleResponseErrorUtil } from '../utils/error.utils';
import { delay } from '../utils/helper';
import getLessonFormat from '../utils/lesson-format/getLessonFormat';
import { mapChatParamValues } from '../utils/page.utils';
import { isFeaturePremium } from '../utils/premium';
import { processContentSection } from '../utils/processContentSection';
import { getIsResponsePopulated } from '../utils/responsePopulatedUtil';
import { getUserCredit } from '../utils/server-side/credits';
import {
  searchWithPerplexity,
  searchWithSerper,
} from '../utils/server-side/unifiedSearch';
import { detectSubjectOrTopic } from '../utils/subjectDetection';
import { EVENTS, trackEvent } from '../utils/tracking';

import GeneratePremiumConfirmationModal from './GeneratePremiumConfirmationModal';
import {
  ErrorModalDetails,
  type ErrorDetails,
  type ErrorWithCode,
} from './organisms/ErrorModalDetails';
import ModalAlerts from './organisms/Modal/ModalAlerts';
import PopUpModal from './PopUpModal';
import SEO from './SEO';

const AlertModal = lazy(() => import('./AlertModal'));
const AuthLayout = lazy(() =>
  import('./AuthLayout').then((mod) => ({ default: mod.AuthLayout })),
);
const Description = lazy(() => import('./Description'));
const ResultsContainer = lazy(() => import('./organisms/ResultsContainer'));
const SignInBtn = lazy(() =>
  import('./SigninBtn').then((mod) => ({ default: mod.SignInBtn })),
);

interface MainContentType {
  banner?: React.ReactNode;
  children: React.ReactNode;
  description?: string;
  creditCost?: number;
  formContents: FormContents;
  pageKey: PageKey;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

function MainContent({
  banner,
  children,
  description,
  formContents,
  pageKey,
  creditCost,
  setIsLoading,
}: MainContentType): JSX.Element {
  const { status, data } = useSession();
  const isSignedIn = status === 'authenticated';
  const isAuthLoading = status === 'loading';
  const { refetch: refetchUserCredit } = getUserCredit();
  const { context: creditContext } = useContext(CreditContext);
  const { isUserPremium, userCredit } = creditContext;
  const router = useRouter();
  const { selectedPortal } = useContext(PortalContext);
  const contentProperties = pageContentLocalStorageProperties[pageKey];

  const redirectId = router?.query?.redirectId as OrganizationSource;

  const learningCompetencies =
    CerebroLessonPlanProperties.learningCompetencies in formContents
      ? formContents.learningCompetencies
      : [];

  let language: string;
  const properties = [
    LessonPlanProperties,
    ObjectiveProperties,
    QuizMakerSingleProperties,
    AnalogyContextualizerProperties,
    DllProperties,
    CatchUpFridaysProperties,
    SummativeTestProperties,
  ];

  for (const property of properties) {
    if (property.language in formContents) {
      language = formContents[property.language] as string;
      break;
    }
  }

  let gradeLevels: string;
  if (DllProperties.language in formContents) {
    gradeLevels = formContents[DllProperties.gradeLevel] as string;
  }

  const containerRef = useRef(null);

  const [responseToShow, setResponseToShow] = useState<string>('');
  const [lessonPlanContent, setLessonPlanContent] = useState<string>();
  const [isStreamDone, setIsStreamDone] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);

  const { isLoading, mutateAsync } = streamChatCompletion(() => openModal());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [conversation, setConversation] = useState<ChatConversation>(null);
  const [isNoCreditsError, setIsNoCreditsError] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const aiResponseText = getAiMessage([...(conversation?.messages ?? [])]);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [pageTitle, setPageTitle] = useState<string>('');
  const [abortController, setAbortController] = useState<AbortController>(null);
  const [subject, setSubject] = useState<string>('');
  const [lessonFormat, setLessonFormat] = useState<string>('');

  const [isDoneRequesting, setIsDoneRequesting] = useState<boolean>(true);
  const [isDllContinuation, setIsDllContinuation] = useState<boolean>(false);
  // used for tracking more activities button
  const [hasBeenClickedInSession, setHasBeenClickedInSession] = useState(false);
  // localStorage
  const [autoGenerate, setAutoGenerate, removeAutoGenerate] = useLocalStorage(
    'autoGenerate',
    false,
  );
  const [exploredLinks, setExploredLinks] = useState<SearchResult[]>([]);
  const { setItemByKey, removeItemByKey } = useLocalStorageItem();

  // Refs for Perplexity streaming
  const perplexityContentRef = useRef('');
  const perplexitySearchResultsRef = useRef<
    { url: string; title: string; date?: string }[]
  >([]);

  // Ref-based mounting state
  const isMountedRef = useRef(true);

  const ERROR_MODAL_TEXT = isModalOpen
    ? 'An error has occurred and we have refunded your credits. Please try again later.'
    : 'An error has occurred and we have refunded your credits. Please try again later.';
  // Preserving this text for usage in the future
  // : 'Looks like something went wrong while trying to generate a lesson plan. Please try again later.';

  const DLL_MAX_OBJECTIVES_PER_CALL = 3;

  const { shouldUseCache, existingGenerationId } = useShouldUseCache();

  const responseLessonFormat =
    pageKey === PageKey.lessonPlan
      ? (formContents as lessonPlanProperties).lpLessonFormat
      : undefined;

  const premiumRoutes = useGetPremiumRoutes();
  const currentRoute = PAGE_KEY_TO_ROUTE_MAP[pageKey];
  const feature = routeToFeatureType[currentRoute];
  const isPremiumRoute = useMemo(
    () => Boolean(currentRoute && premiumRoutes.includes(currentRoute)),
    [premiumRoutes, currentRoute],
  );

  useGenerationLoader({ setResponseToShow });

  useCerebroRedirect({
    isSignedIn,
    data,
    redirectId,
    router,
  });

  useUpdateLoadingState(
    isLoading,
    setIsLoading,
    isStreamDone,
    isDoneRequesting,
  );

  useEffect(() => {
    if (!isSignedIn && !isAuthLoading) {
      // Reset stored values when user signs out
      contentProperties &&
        Object.keys(contentProperties).forEach(function (key) {
          removeItemByKey(contentProperties[key]);
        });
    } else if (isSignedIn && autoGenerate && creditContext.totalCredits > 0) {
      removeAutoGenerate();
      handleSubmit();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, creditContext]);

  useLocalStorageInitialization({
    isSignedIn,
    isAuthLoading,
    contentProperties,
    formContents,
    isStreamDone,
    removeItemByKey,
    setItemByKey,
  });

  useResponseHandling({
    conversation,
    lessonPlanContent,
    aiResponseText,
    setResponseToShow,
    responseToShow,
    lessonFormat: responseLessonFormat,
  });

  useGenerationSave({
    isStreamDone,
    isDoneRequesting,
    responseToShow,
    pageKey,
    selectedPortal,
    isLoading,
    subject,
    isDllContinuation,
    existingGenerationId,
    shouldUseCache,
    inputs: formContents,
  });

  // Cleanup effect to track mounting state
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  function openModal() {
    return !isModalOpen && setIsModalOpen(true);
  }

  async function createStreamReaderFromAppCompletionResponse(
    appCompletionResponse,
  ) {
    const reader = appCompletionResponse.getReader();
    return reader;
  }

  function handleRedirectToTopup() {
    const paymentPageUrl = routes.PRICING;
    const eventMessage =
      currentRoute && isPremiumRoute
        ? TOP_UP_TRACKING.GENERATE_PREMIUM_PLAN_MODAL
        : TOP_UP_TRACKING.GENERATE_PLAN_MODAL;

    trackEvent(EVENTS.CLICK_TOPUP_LINK, {
      from: eventMessage,
    });
    window.open(paymentPageUrl, '_blank');
    setIsNoCreditsError(false);
  }

  function handleCancelTopupModal() {
    setIsNoCreditsError(false);
    setHasError(false);
  }

  const handleSignIn = () => {
    if (checkInAppBrowser()) {
      setPopUpData((prev) => ({ ...prev, isOpen: true }));
      return;
    }
    setIsLoginModalOpen(false);

    signInUser(null, () => setAutoGenerate(true));
  };

  // might be used in the future if we need to check if there are missing inputs. Some inputs are not required so this is not used for now.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function hasMissingInput() {
    for (const property in formContents) {
      if (!formContents[property]) {
        return true;
      }
    }

    return false;
  }

  const { isGeneratePaused, disabledGenerate, enableGenerate } =
    useGeneratePause();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  function handleConfirmClose() {
    setShowConfirmationModal(false);

    handleSubmit();
  }

  function handleGenerate(event: React.FormEvent | null) {
    if (event) {
      event.preventDefault();
    }

    if (getIsResponsePopulated()) {
      setShowConfirmationModal(true);
      return;
    }

    const generatingLessonPlanWithReferences =
      isUserPremium && pageKey === PageKey.lessonPlan;
    const isHighCostFeature = creditCost > 1;

    if (
      (isFeaturePremium(pageKey) && creditCost > userCredit?.paidCredits) ||
      (isHighCostFeature &&
        (isFeaturePremium(pageKey) || generatingLessonPlanWithReferences))
    ) {
      if (!isSignedIn) {
        setIsLoginModalOpen(true);
        return;
      }
      setIsConfirmationModalOpen(true);
      return;
    }

    handleSubmit();
  }

  const { trackGenerationAttempt, trackGenerationSuccess } =
    useGenerationTracking({
      pageKey,
      formContents,
      isPremium: !!userCredit?.paidCredits,
    });
  const { popUpData, setPopUpData } = useInAppBrowser();
  async function handleSubmit(): Promise<void> {
    if (isGeneratePaused) {
      return;
    }

    setHasBeenClickedInSession(false);
    setHasError(false);
    setIsNoCreditsError(false);
    setErrorDetails(null);
    setResponseToShow('');
    setLessonPlanContent('');
    setIsStreamDone(false);
    setIsDoneRequesting(false);
    setIsDllContinuation(false);
    setConversation(null);
    setTitleAndPageTitle();

    disabledGenerate();

    if (checkInAppBrowser()) {
      setPopUpData((prev) => ({ ...prev, isOpen: true }));
      return;
    }
    if (!isSignedIn) {
      setIsLoginModalOpen(true);
      return;
    }
    if (!isStreamDone) {
      await stopGeneration();
      await delay(200);
    }

    removeAutoGenerate();
    trackGenerationAttempt();
    setIsDoneRequesting(false);
    setIsNoCreditsError(false);
    setHasError(false);
    setIsConfirmationModalOpen(false);

    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });

    try {
      setConversation(null);
      setIsStreamDone(false);
      setLessonPlanContent(null);

      // check user credits
      if (creditContext.totalCredits === 0) {
        setIsNoCreditsError(true);
        return;
      }

      openModal();

      let dllDays = 0;
      if (pageKey === PageKey.dailyLessonLog) {
        const dllObjectives: DLLObjective[] =
          formContents[DllProperties.objectives];
        dllDays = dllObjectives.filter((day) => day.dayObjectives).length;
      }

      const newLessonFormat = getLessonFormat(
        formContents,
        pageKey,
        language,
        gradeLevels,
        dllDays || undefined,
        selectedPortal,
        isUserPremium,
      );

      setLessonFormat(newLessonFormat);

      try {
        if (newLessonFormat === TOPIC_EXPLORER_PROMPT_NAME_PREMIUM) {
          const topic = (formContents as topicExplorerProperties).topic;
          const result = await searchWithPerplexity(topic, true);
          await refetchUserCredit();

          // Handle streaming response
          if (result instanceof ReadableStream) {
            setIsModalOpen(true);
            setIsStreamDone(false);

            const reader =
              await createStreamReaderFromAppCompletionResponse(result);
            const decoder = new TextDecoder();
            let streamDone = false;

            // Reset refs for new stream
            perplexityContentRef.current = '';
            perplexitySearchResultsRef.current = [];

            while (!streamDone) {
              const { done, value } = await reader.read();
              streamDone = done;

              if (streamDone) {
                if (isMountedRef.current) {
                  setIsDoneRequesting(true);
                  setIsStreamDone(true);
                }
                break;
              }

              if (isMountedRef.current) {
                handlePerplexityStreamUpdate(
                  value,
                  decoder,
                  setResponseToShow,
                  setExploredLinks,
                  perplexityContentRef,
                  perplexitySearchResultsRef,
                );
              }

              await delay();
            }
            return;
          }

          // Handle non-streaming response (fallback)
          if (!result || !result.choices || result.choices.length === 0) {
            throw new Error('Invalid response: No choices available');
          }

          const firstChoice = result.choices[0];
          if (!firstChoice?.message?.content) {
            throw new Error('Invalid response: No message content available');
          }

          const searchResults = result.search_results || [];
          setExploredLinks(
            searchResults.map((link) => ({
              link: link.url,
              title: link.title,
              date: link.date,
            })),
          );

          const response = firstChoice.message.content;
          setResponseToShow(response);
          setIsModalOpen(true);
          setIsDoneRequesting(true);
          setIsStreamDone(true);
          return;
        }
      } catch (error) {
        handleResponseError(error);
        setIsDoneRequesting(true);
        setIsStreamDone(true);
        return;
      }

      let fetchedSerperLinks: SearchResult[] = [];

      try {
        if (pageKey === PageKey.unitPlan) {
          const { topic, gradeLevel, subject } =
            formContents as unitPlanProperties;
          const serperLinkTopic = `${topic} for learners in ${gradeLevel} in ${subject}`;
          const result = await searchWithSerper(serperLinkTopic);
          // Store the result and set states for display
          fetchedSerperLinks = result;
          setExploredLinks(result as SearchResult[]);
        }
      } catch (error) {
        handleResponseError(error);
      }

      const paramValues = mapChatParamValues(
        formContents,
        newLessonFormat,
        fetchedSerperLinks,
        selectedPortal,
      );

      let dllContinuationObjectives;

      if (pageKey === PageKey.dailyLessonLog) {
        dllContinuationObjectives = paramValues.objectives
          .split(' -|; ')
          .slice(DLL_MAX_OBJECTIVES_PER_CALL)
          .join(' -|; ');

        const dllInitialObjectives = paramValues.objectives
          .split(' -|; ')
          .slice(0, DLL_MAX_OBJECTIVES_PER_CALL)
          .join(' -|; ');
        paramValues.objectives = dllInitialObjectives;
      }

      const chatParams = {
        values: paramValues,
      };

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      const appCompletionResponse = (await mutateAsync({
        chatParams: {
          values: paramValues,
          isCache: shouldUseCache,
        },
        promptname: newLessonFormat,
        feature: feature,
        abortController: newAbortController,
      })) as unknown;

      await refetchUserCredit();

      const reader = await createStreamReaderFromAppCompletionResponse(
        appCompletionResponse,
      );

      const decoder = new TextDecoder();
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();

        streamDone = done;

        if (streamDone) {
          if (
            pageKey === PageKey.dailyLessonLog &&
            creditCost > DLL_MAX_OBJECTIVES_PER_CALL
          ) {
            try {
              setIsDllContinuation(true);
              await handleDllContinuationResponse({
                values: {
                  ...chatParams.values,
                  objectives: dllContinuationObjectives,
                },
              });
            } catch (error) {
              handleResponseError(error);
            }
          }
          if (isMountedRef.current) {
            setIsDoneRequesting(true);
            setIsStreamDone(true);
          }
          break;
        }

        await delay();
        handleStreamUpdate(value, decoder);
      }
      trackGenerationSuccess();
    } catch (error) {
      handleResponseError(error);
    } finally {
      setIsDoneRequesting(true);
      setIsStreamDone(true);
      enableGenerate();
    }
  }

  async function handleUserResponse(
    event: React.FormEvent,
    topic: string,
    actionType: string,
    newActivityNumber: number,
  ): Promise<boolean> {
    let isSuccess = true;
    setHasError(false);

    try {
      event.preventDefault();
      setIsStreamDone(false);

      disabledGenerate();

      const isLessonPlan = pageKey === PageKey.lessonPlan;
      const hasSufficientCredits = userCredit?.paidCredits > 1;
      const hasAtleastOneCreditEach =
        userCredit?.paidCredits > 0 && userCredit?.credits > 0;
      const canGenerateLp = hasSufficientCredits || hasAtleastOneCreditEach;

      if (isLessonPlan && !canGenerateLp) {
        setIsNoCreditsError(true);
        return false;
      }

      let promptToBeUsed =
        actionType === PROMPT_ACTION.elaborate
          ? PROMPT_TO_BE_USED.elaborate
          : PROMPT_TO_BE_USED.examples;

      // Process content in real-time as it streams in
      const checker = (
        formContents as lessonPlanProperties
      ).lpLessonFormat?.trim();

      //special case for iPlan
      if (promptToBeUsed === PROMPT_TO_BE_USED.examples) {
        if (checker === LessonFormat.CODE_IPLAN) {
          promptToBeUsed = PROMPT_TO_BE_USED.examplesIPlan;
        }
      }

      let newLessonFormat = promptToBeUsed;

      if (language === 'Filipino') {
        newLessonFormat = 'filipinoFirst' + promptToBeUsed;
      }
      setLessonFormat(newLessonFormat);
      const newUserConversationMessage: ChatMessage = {
        role: ChatCompletionResponseMessageRoleEnum.User,
        content: promptToBeUsed,
      };

      setConversation((conversation) => ({
        ...conversation,
        messages: [...conversation.messages, newUserConversationMessage],
      }));

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      const chatParams = {
        values: {
          ...mapChatParamValues(formContents, newLessonFormat),
          newActivityNumber,
        },
        isCache: shouldUseCache,
      };

      const appCompletionResponse = (await mutateAsync({
        chatParams,
        promptname: newLessonFormat,
        abortController: newAbortController,
      })) as ReadableStream<Uint8Array>;

      await refetchUserCredit();

      let streamDone = false;
      const reader = await createStreamReaderFromAppCompletionResponse(
        appCompletionResponse,
      );

      const decoder = new TextDecoder();
      let accumulatedResponse = '';

      while (!streamDone) {
        const { done, value } = await reader.read();

        streamDone = done;

        if (streamDone) {
          setIsStreamDone(true);
          break;
        }

        await delay();

        const chunk = decoder.decode(value);
        accumulatedResponse += chunk;

        const role = ChatCompletionResponseMessageRoleEnum.Assistant;

        setConversation((conversation) => {
          const lastMessage = conversation?.messages?.[
            conversation.messages.length - 1
          ] ?? {
            role: ChatCompletionResponseMessageRoleEnum.User,
            content: '',
          };

          if (
            lastMessage.role !== ChatCompletionResponseMessageRoleEnum.Assistant
          ) {
            return {
              ...conversation,
              messages: [
                ...conversation.messages,
                {
                  role,
                  content: chunk,
                },
              ],
            };
          }

          return {
            ...conversation,
            messages: [
              ...conversation.messages.slice(0, -1),
              {
                ...lastMessage,
                content: lastMessage?.content + chunk,
              },
            ],
          };
        });

        let activityNum = 0;

        if (checker === LessonFormat.CODE_M) {
          activityNum = extractActivityNumber(
            topic,
            TARGET_SECTION.DEVELOPING_UNDERSTANDING_OF_THE_KEY_IDEA,
            TARGET_SECTION.PAGPAPAUNLAD_NG_KAALAMAN,
          );
        } else {
          activityNum = extractActivityNumber(topic);
        }

        // Responsible for appending new content
        if (
          [LessonFormat.CODE_5E, LessonFormat.CODE_7E].includes(
            checker as LessonFormat,
          )
        ) {
          if (promptToBeUsed === PROMPT_TO_BE_USED.examples) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.EXPLORE,
              ANCHOR_KEYWORD.EXPLAIN,
              accumulatedResponse,
              (section, response) =>
                modifyActivities(
                  section,
                  response,
                  ACTIVITY_SECTION_PER_FORMAT[LessonFormat.CODE_5E],
                ),
              activityNum,
            );
            setLessonPlanContent(newContent);
          } else if (promptToBeUsed === PROMPT_TO_BE_USED.elaborate) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.EXPLORE,
              ANCHOR_KEYWORD.EXPLAIN,
              accumulatedResponse,
              elaborateExploreSection5OR7E,
              activityNum,
            );
            setLessonPlanContent(newContent);
          }
        } else if (checker === LessonFormat.CODE_4A) {
          if (promptToBeUsed === PROMPT_TO_BE_USED.examples) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.ACTIVITY,
              ANCHOR_KEYWORD.ANALYSIS,
              accumulatedResponse,
              (section, response) =>
                modifyActivities(
                  section,
                  response,
                  ACTIVITY_SECTION_PER_FORMAT[LessonFormat.CODE_4A],
                ),
              activityNum,
            );
            setLessonPlanContent(newContent);
          } else if (promptToBeUsed === PROMPT_TO_BE_USED.elaborate) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.ACTIVITY,
              ANCHOR_KEYWORD.ANALYSIS,
              accumulatedResponse,
              elaborateActivitySection4A,
              activityNum,
            );
            setLessonPlanContent(newContent);
          }
        } else if (checker === LessonFormat.CODE_IPLAN) {
          if (promptToBeUsed === PROMPT_TO_BE_USED.examplesIPlan) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.ACTIVITY,
              ANCHOR_KEYWORD.ANALYSIS,
              accumulatedResponse,
              (section, response) =>
                modifyActivities(
                  section,
                  response,
                  ACTIVITY_SECTION_PER_FORMAT[LessonFormat.CODE_IPLAN],
                ),
              activityNum,
            );
            setLessonPlanContent(newContent);
          } else if (promptToBeUsed === PROMPT_TO_BE_USED.elaborate) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.ACTIVITY,
              ANCHOR_KEYWORD.ANALYSIS,
              accumulatedResponse,
              elaborateActivitySection4A,
              activityNum,
            );
            setLessonPlanContent(newContent);
          }
        } else if (checker === LessonFormat.CODE_M) {
          if (promptToBeUsed === PROMPT_TO_BE_USED.examples) {
            const newContent = processContentSection(
              lessonPlanContent,
              responseToShow,
              TARGET_SECTION.DEVELOPING_UNDERSTANDING_OF_THE_KEY_IDEA,
              ANCHOR_KEYWORD.AFTER_POST_LESSON_PROPER,
              accumulatedResponse,
              (section, response) =>
                modifyActivities(
                  section,
                  response,
                  ACTIVITY_SECTION_PER_FORMAT[LessonFormat.CODE_M],
                ),
              activityNum,
            );
            setLessonPlanContent(newContent);
          }
        }
      }
    } catch (error) {
      isSuccess = false;
      handleResponseError(error);
    } finally {
      setIsDoneRequesting(true);
      setIsStreamDone(true);
      enableGenerate();
    }

    return isSuccess;
  }

  async function handleDllContinuationResponse(params: {
    values: {
      objectives: string;
      [key: string]: unknown;
    };
  }): Promise<boolean> {
    let isSuccess = true;
    setHasError(false);

    try {
      setIsStreamDone(false);

      const remainingObjectivesCount =
        params.values.objectives.split(' -|; ').length; // determines the format and cost
      const dllFormat = (formContents as dllProperties).dllFormat;
      const dllLanguage = (formContents as dllProperties).dllLanguage;
      let continuationPrompt = `${PROMPT_TO_BE_USED.continuation}${remainingObjectivesCount}`;
      if (dllFormat === DLLFormat.MATATAG) {
        continuationPrompt = `${PROMPT_TO_BE_USED.matatagContinuation}${remainingObjectivesCount}`;
      }
      if (dllLanguage === Language.FILIPINO) {
        continuationPrompt = 'filipinoFirst' + continuationPrompt;
      }

      const newAbortController = new AbortController();
      setAbortController(newAbortController);
      const continuationFeature = FeatureType.DLL_CONTINUATION;

      const appCompletionResponse = (await mutateAsync({
        chatParams: {
          values: {
            ...params.values,
          },
          isCache: shouldUseCache,
        },
        promptname: continuationPrompt,
        feature: continuationFeature,
        abortController: newAbortController,
      })) as ReadableStream<Uint8Array>;

      let streamDone = false;
      const reader = await createStreamReaderFromAppCompletionResponse(
        appCompletionResponse,
      );

      const decoder = new TextDecoder();

      while (!streamDone) {
        const { done, value } = await reader.read();

        streamDone = done;

        if (streamDone) {
          setIsStreamDone(true);
          break;
        }

        await delay();

        const chunk = decoder.decode(value);

        setResponseToShow((prev) => prev + chunk);

        const role = ChatCompletionResponseMessageRoleEnum.Assistant;

        setConversation((conversation) => {
          const lastMessage = conversation?.messages?.[
            conversation.messages.length - 1
          ] ?? {
            role: ChatCompletionResponseMessageRoleEnum.User,
            content: '',
          };

          if (
            lastMessage.role !== ChatCompletionResponseMessageRoleEnum.Assistant
          ) {
            return {
              ...conversation,
              messages: [
                ...conversation.messages,
                {
                  role,
                  content: chunk,
                },
              ],
            };
          }

          return {
            ...conversation,
            messages: [
              ...conversation.messages.slice(0, -1),
              {
                ...lastMessage,
                content: lastMessage?.content + chunk,
              },
            ],
          };
        });
      }
    } catch (error) {
      isSuccess = false;
      handleResponseError(error);
    } finally {
      setIsDoneRequesting(true);
      setIsStreamDone(true);
      setIsDllContinuation(false);
    }

    return isSuccess;
  }

  function handleResponseError(error: ErrorWithCode) {
    const { isNoCreditsError, hasError, errorDetails } =
      handleResponseErrorUtil(error);
    setIsNoCreditsError(isNoCreditsError);
    setHasError(hasError);
    setErrorDetails(errorDetails);
  }

  function setTitleAndPageTitle() {
    const { title, pageTitle } = PAGE_KEY_TITLES[pageKey] || {
      title: '',
      pageTitle: '',
    };
    setTitle(title);
    setPageTitle(pageTitle);

    const detectedSubject = detectSubjectOrTopic(formContents);
    if (detectedSubject) {
      setSubject(detectedSubject);
    }
  }

  const stopGeneration = async () => {
    await refetchUserCredit();
    console.log('Aborting...');
    if (abortController) {
      abortController.abort();
      console.log('Generation aborted.');
    }
    enableGenerate();
  };

  const { handleStreamUpdate } = useStreamHandler(setConversation);
  const { handlePerplexityStreamUpdate } = usePerplexityStreamHandler();

  return (
    <>
      <SEO pageKey={pageKey} />
      <AuthLayout
        banner={banner}
        containerRef={containerRef}
        renderResults={() =>
          responseToShow && (
            <ResultsContainer
              containerRef={containerRef}
              exploredLinks={exploredLinks}
              formContents={formContents}
              handleUserResponse={handleUserResponse}
              hasBeenClickedInSession={hasBeenClickedInSession}
              hasGenerationError={hasError}
              isDoneRequesting={isDoneRequesting}
              isNoCreditsError={isNoCreditsError}
              isStreamDone={isStreamDone}
              isUserPremium={isUserPremium}
              learningCompetencies={learningCompetencies}
              lessonFormat={lessonFormat}
              open={isModalOpen}
              pageKey={pageKey}
              responseToShow={responseToShow}
              setHasBeenClickedInSession={setHasBeenClickedInSession}
              setHasError={setHasError}
              setIsNoCreditsError={setIsNoCreditsError}
              setOpen={setIsModalOpen}
              setResponseToShow={setResponseToShow}
              subject={subject}
              title={title}
              onCloseModal={stopGeneration}
            />
          )
        }
      >
        <Description description={description} />

        <form
          action="#"
          className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2"
          onSubmit={handleGenerate}
        >
          {children}
          <div className="col-span-full">
            {hasError && isNoCreditsError && (
              <p className="text-blue-700">
                Not enough credits. You can{' '}
                <Link
                  className="cursor-pointer text-blue-700 underline hover:text-blue-600"
                  href={routes.PRICING}
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={() =>
                    trackEvent(EVENTS.CLICK_TOPUP_LINK, {
                      from: TOP_UP_TRACKING.NOT_ENOUGH_CREDITS_FOOTER,
                    })
                  }
                >
                  top up here.
                </Link>
              </p>
            )}
          </div>
        </form>
      </AuthLayout>

      <PopUpModal {...popUpData} setIsOpen={setPopUpData} />
      <AlertModal
        hasDivider
        hideCloseIcon
        isSingleButton
        isOpen={hasError && !isNoCreditsError}
        titleText="Something went wrong"
        variant="error"
        bodyText={
          errorDetails ? (
            <ErrorModalDetails error={errorDetails} />
          ) : (
            ERROR_MODAL_TEXT
          )
        }
        onCancel={() => setHasError(false)}
        onConfirm={() => {
          if (isModalOpen) {
            setHasError(false);
          } else {
            window.location.reload();
          }
        }}
      />

      <AlertModal
        hasDivider
        bodyText={`Please top-up to continue generating ${pageTitle}.`}
        confirmButtonText="Top Up"
        isOpen={isNoCreditsError && !isModalOpen}
        titleText="Not Enough Credits"
        onCancel={handleCancelTopupModal}
        onConfirm={handleRedirectToTopup}
      />

      <AlertModal
        hasDivider
        bodyText="To access this app's features, please log in with your google account."
        isOpen={isLoginModalOpen}
        titleText="Please log in to use this app."
        confirmButtonComponent={
          <SignInBtn
            handleSignIn={handleSignIn}
            width="w-full sm:w-auto justify-center"
          />
        }
        onCancel={() => setIsLoginModalOpen(false)}
      />

      <GeneratePremiumConfirmationModal
        creditCost={creditCost}
        isOpen={isConfirmationModalOpen}
        pageKey={pageKey}
        onCancel={() => setIsConfirmationModalOpen(false)}
        onProceed={handleSubmit}
        onRedirectToTopup={handleRedirectToTopup}
      />

      <ModalAlerts
        handleConfirmClose={handleConfirmClose}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />
    </>
  );
}

export default MainContent;
